import React, { useRef, useState } from 'react';
import { CheckCircle2, FileImage, FileUp, Printer as ScannerIcon, ScanLine, Smartphone, Trash2, UploadCloud } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { PageHeader } from '../../components/admin/PageHeader';
import { WorkflowStepper } from '../../components/admin/WorkflowStepper';
import { Panel } from '../../components/Panel';
import { A4Frame } from '../../components/notice/A4Frame';
import { ScannedNotice } from '../../components/notice/ScannedNotice';
import { useDemo } from '../../contexts/DemoContext';
import { trafficDivisions } from '../../data/notices';
import { toBn } from '../../utils/bangla';
type Method = 'scanner' | 'file' | 'mobile';
const methods: {
  id: Method;
  label: string;
  desc: string;
  icon: typeof FileUp;
}[] = [{
  id: 'scanner',
  label: 'স্ক্যানার থেকে আপলোড',
  desc: 'সংযুক্ত ডকুমেন্ট স্ক্যানার',
  icon: ScannerIcon
}, {
  id: 'file',
  label: 'PDF/ছবি আপলোড',
  desc: 'PDF, JPG, PNG (সর্বোচ্চ ১০MB)',
  icon: FileUp
}, {
  id: 'mobile',
  label: 'মোবাইল অ্যাপ থেকে গ্রহণ',
  desc: 'অফিসার অ্যাপে তোলা ছবি',
  icon: Smartphone
}];
const selectCls = 'h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1769AA]';
export function NoticeUpload() {
  const navigate = useNavigate();
  const {
    user,
    completeStage,
    resetWorkflow
  } = useDemo();
  const [method, setMethod] = useState<Method>('file');
  const [file, setFile] = useState<{
    name: string;
    size: string;
  } | null>(null);
  const [drag, setDrag] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const accept = (name: string, bytes: number) => {
    if (!/\.(pdf|jpe?g|png)$/i.test(name)) return setError('অসমর্থিত ফাইল। শুধুমাত্র PDF, JPG বা PNG ফাইল আপলোড করুন।');
    setError('');
    setUploaded(false);
    setFile({
      name,
      size: `${toBn((bytes / 1024 / 1024).toFixed(2))} MB`
    });
  };
  const useSample = (name = 'DMP_Notice_0614_0748_scan.pdf') => {
    setError('');
    setUploaded(false);
    setFile({
      name,
      size: '১.৪২ MB'
    });
  };
  const upload = () => {
    if (!file) return setError('প্রথমে একটি ফাইল নির্বাচন করুন।');
    resetWorkflow();
    setProgress(0);
    let p = 0;
    const t = setInterval(() => {
      p += 20;
      setProgress(p);
      if (p >= 100) {
        clearInterval(t);
        setProgress(null);
        setUploaded(true);
        completeStage(1);
        toast.success('নোটিশ সফলভাবে আপলোড হয়েছে', {
          description: 'ডকুমেন্ট ID: UPL-2025-0614-00748'
        });
      }
    }, 250);
  };
  return <div data-id="element-1237" data-mp="1eqy1uz3t1f8p">
      <PageHeader screen="WEB SCREEN 04" title="নতুন প্রসিকিউশন নোটিশ আপলোড" subtitle="AI প্রসিকিউশন ব্যবস্থায় মুদ্রিত মূল নোটিশ স্ক্যান/আপলোড করে ডিজিটাইজেশন শুরু করুন" data-id="element-1238" data-mp="1esr7cb2oltf4" />
      <WorkflowStepper current={0} data-id="element-1239" data-mp="12hqfkul6avb0" />

      <div className="grid gap-3 md:grid-cols-3" data-id="element-1240" data-mp="12goxw79njakb">
        {methods.map(m => {
        const Icon = m.icon;
        const active = method === m.id;
        return <button key={m.id} onClick={() => setMethod(m.id)} className={`flex items-center gap-3 rounded-lg border-2 bg-white p-4 text-left transition-colors ${active ? 'border-[#003B71] bg-[#E8F1FA]/40' : 'border-border hover:border-[#1769AA]/50'}`} data-id="element-1241" data-mp="thld8yuu6n3p">
              <span className={`flex h-11 w-11 items-center justify-center rounded-lg ${active ? 'bg-[#003B71] text-white' : 'bg-[#F4F7FB] text-[#003B71]'}`} data-id="element-1242" data-mp="1l659pqqsepg1"><Icon className="h-5 w-5" data-id="element-1243" data-mp="1c8304mszmoh5" /></span>
              <span data-id="element-1244" data-mp="1c9e3iliitu7q">
                <span className="block font-semibold text-[#003B71]" data-id="element-1245" data-mp="5r7p3jfdh8ee">{m.label}</span>
                <span className="text-xs text-slate-500" data-id="element-1246" data-mp="5ov2uu2l0bvj">{m.desc}</span>
              </span>
            </button>;
      })}
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-[1.3fr_1fr]" data-id="element-1247" data-mp="1onrnceefyxm5">
        <Panel title="ডকুমেন্ট" data-id="element-1248" data-mp="1tfj6i3v9q129">
          {method === 'file' && <div onDragOver={e => {
          e.preventDefault();
          setDrag(true);
        }} onDragLeave={() => setDrag(false)} onDrop={e => {
          e.preventDefault();
          setDrag(false);
          const f = e.dataTransfer.files[0];
          if (f) accept(f.name, f.size);
        }} className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${drag ? 'border-[#1769AA] bg-[#E8F1FA]' : 'border-slate-300 bg-[#F4F7FB]'}`} data-id="element-1249" data-mp="1tguaa3opr8td">
              <UploadCloud className="h-10 w-10 text-[#1769AA]" data-id="element-1250" data-mp="23c5pso7y4k26" />
              <p className="mt-3 font-medium" data-id="element-1251" data-mp="mz7bucrnn1bf">ফাইল এখানে টেনে এনে ছেড়ে দিন</p>
              <p className="text-sm text-slate-500" data-id="element-1252" data-mp="ryaanfh1kbay">অথবা</p>
              <div className="mt-3 flex flex-wrap justify-center gap-2" data-id="element-1253" data-mp="rzv4f2vj7q7r">
                <Button variant="outline" onClick={() => inputRef.current?.click()} data-id="element-1254" data-mp="jg88fw6xxt18">ফাইল নির্বাচন করুন</Button>
                <Button variant="secondary" onClick={() => useSample()} data-id="element-1255" data-mp="ffejr0loca7a">নমুনা নোটিশ ব্যবহার করুন</Button>
              </div>
              <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => {
            const f = e.target.files?.[0];
            if (f) accept(f.name, f.size);
          }} data-id="element-1256" data-mp="tdmm5o1hc3pu" />
              <p className="mt-3 text-xs text-slate-500" data-id="element-1257" data-mp="rbfbvna56kl">সমর্থিত ফরম্যাট: PDF, JPG, PNG</p>
            </div>}
          {method === 'scanner' && <div className="rounded-lg border border-border bg-[#F4F7FB] p-6 text-center" data-id="element-1258" data-mp="ah4okpzy99lo">
              <ScanLine className="mx-auto h-10 w-10 text-[#1769AA]" data-id="element-1259" data-mp="5pmmtzq3r4zn" />
              <p className="mt-2 font-medium" data-id="element-1260" data-mp="5objg10kjz92">সংযুক্ত স্ক্যানার: Fujitsu fi-7160 (ট্রাফিক সদর দপ্তর-০২)</p>
              <p className="text-sm text-slate-500" data-id="element-1261" data-mp="396v2ccxcxsr7">রেজোলিউশন ৩০০ DPI • রঙিন • ডুপ্লেক্স বন্ধ</p>
              <Button className="mt-4" onClick={() => {
            toast.info('স্ক্যান করা হচ্ছে…');
            setTimeout(() => useSample('SCAN_20250614_101204.pdf'), 900);
          }} data-id="element-1262" data-mp="1d3qef79ghulx">স্ক্যান শুরু করুন</Button>
            </div>}
          {method === 'mobile' && <ul className="divide-y divide-border rounded-lg border border-border" data-id="element-1263" data-mp="1mutdk3itrzik">
              {[['MOB-CAP-2025-0614-118', 'এএসআই মোঃ রাকিবুল হাসান', '১৪/০৬/২০২৫, ১০:০৪'], ['MOB-CAP-2025-0614-117', 'কনস্টেবল মোঃ হাবিবুর রহমান', '১৪/০৬/২০২৫, ০৯:৫১']].map(([id, by, at]) => <li key={id} className="flex items-center justify-between gap-3 p-3" data-id="element-1264" data-mp="z61rzuzqqfer">
                  <div className="flex items-center gap-3" data-id="element-1265" data-mp="133zkp7spzjmw">
                    <FileImage className="h-8 w-8 text-[#006A4E]" data-id="element-1266" data-mp="184vws8n86c3e" />
                    <div data-id="element-1267" data-mp="1829h1lq6kqa3">
                      <div className="font-mono text-sm" data-id="element-1268" data-mp="2el7n5njt2pof">{id}</div>
                      <div className="text-xs text-slate-500" data-id="element-1269" data-mp="2en26ixffi9qe">{by} • {at}</div>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => useSample(`${id}.jpg`)} data-id="element-1270" data-mp="2to92ge4pqmfm">গ্রহণ করুন</Button>
                </li>)}
            </ul>}

          {error && <p role="alert" className="mt-3 rounded-md bg-[#FCE9EA] px-3 py-2 text-sm text-[#B4232A]" data-id="element-1271" data-mp="2jxo6kygfbw0e">{error}</p>}

          {file && <div className="mt-5 grid gap-4 md:grid-cols-[180px_1fr]" data-id="element-1272" data-mp="2fv9qgm453act">
              <div className="overflow-hidden rounded-md border border-border bg-slate-100 p-2" data-id="element-1273" data-mp="2axrc2g7cs2xf">
                <A4Frame data-id="element-1274" data-mp="25yet46jlrg04"><ScannedNotice data-id="element-1275" data-mp="25xlp3dc134lo" /></A4Frame>
              </div>
              <div data-id="element-1276" data-mp="260hq5aj6a9ec">
                <div className="flex items-start justify-between gap-2" data-id="element-1277" data-mp="20yscro8h9jn3">
                  <div data-id="element-1278" data-mp="2114vi3m27y17">
                    <div className="font-medium" data-id="element-1279" data-mp="1wxqd56anf5kl">{file.name}</div>
                    <div className="text-xs text-slate-500" data-id="element-1280" data-mp="1wzjkknt35ttn">{file.size} • ১ পৃষ্ঠা • ৩০০ DPI</div>
                  </div>
                  <button onClick={() => {
                setFile(null);
                setUploaded(false);
              }} className="rounded p-1.5 text-slate-500 hover:bg-muted" aria-label="ফাইল সরান" data-id="element-1281" data-mp="3rbhtspreappj"><Trash2 className="h-4 w-4" data-id="element-1282" data-mp="efwnq16znqgl" /></button>
                </div>
                <div className="mt-3 rounded-md bg-[#F4F7FB] p-3 text-xs text-slate-600" data-id="element-1283" data-mp="igylsep65wgz">
                  ডকুমেন্ট মান যাচাই: <span className="font-medium text-[#16834A]" data-id="element-1284" data-mp="4ptuq0zvaci9">ভালো</span> • স্বয়ংক্রিয় সীমানা শনাক্ত • হেলানো কোণ ০.৩৫°
                </div>
                {progress !== null && <div className="mt-3" data-id="element-1285" data-mp="3qvqt6um1oviy">
                    <div className="mb-1 flex justify-between text-xs" data-id="element-1286" data-mp="3qup61rrr8ikv"><span data-id="element-1287" data-mp="3vv3gfggtw4m2">আপলোড হচ্ছে…</span><span data-id="element-1288" data-mp="3vweh37ez8w8y">{toBn(progress)}%</span></div>
                    <div className="h-2 rounded-full bg-slate-100" data-id="element-1289" data-mp="3vsz367x1ht94"><div className="h-2 rounded-full bg-[#1769AA] transition-all" style={{
                  width: `${progress}%`
                }} data-id="element-1290" data-mp="3hwlthkyqyw57" /></div>
                  </div>}
                {uploaded && <div className="mt-3 flex items-center gap-2 rounded-md border border-[#16834A]/30 bg-[#E7F4EC] px-3 py-2 text-sm text-[#16834A]" data-id="element-1291" data-mp="1g58qx2k950cb">
                    <CheckCircle2 className="h-4 w-4" data-id="element-1292" data-mp="1l0xstcjqutbs" /> আপলোড সম্পন্ন • এনক্রিপ্টেড স্টোরেজে সংরক্ষিত
                  </div>}
              </div>
            </div>}
        </Panel>

        <Panel title="আপলোড তথ্য" data-id="element-1293" data-mp="23ziugaxi2vo9">
          <div className="space-y-4" data-id="element-1294" data-mp="23zsk1xemv0tf">
            <label className="block" data-id="element-1295" data-mp="240tz0cyrs7gf">
              <span className="mb-1 block text-sm font-medium" data-id="element-1296" data-mp="1z92g8oluv60u">আপলোডের তারিখ</span>
              <Input defaultValue="১৪/০৬/২০২৫" className="h-10" data-id="element-1297" data-mp="2dxq8om0emzmk" />
            </label>
            <label className="block" data-id="element-1298" data-mp="2dzt81wng653y">
              <span className="mb-1 block text-sm font-medium" data-id="element-1299" data-mp="28z58unp2ehyn">ট্রাফিক বিভাগ</span>
              <select className={selectCls} defaultValue="তেজগাঁও" data-id="element-1300" data-mp="2my4wpt4r6str">
                {trafficDivisions.map(d => <option key={d} value={d} data-id="element-1301" data-mp="2ix2ur4jgc2o3">{d} ট্রাফিক বিভাগ</option>)}
              </select>
            </label>
            <label className="block" data-id="element-1302" data-mp="sju6bg8ifqlv">
              <span className="mb-1 block text-sm font-medium" data-id="element-1303" data-mp="skvummeje9lj">আপলোডকারী</span>
              <Input value={user?.name ?? ''} readOnly className="h-10 bg-[#F4F7FB]" data-id="element-1304" data-mp="ojtum3csptii" />
            </label>
            <label className="block" data-id="element-1305" data-mp="1bqsvr0dvjo16">
              <span className="mb-1 block text-sm font-medium" data-id="element-1306" data-mp="1bnn5vixf8i4e">ডকুমেন্টের ধরন</span>
              <select className={selectCls} data-id="element-1307" data-mp="17muuok4zo35h">
                <option data-id="element-1308" data-mp="12ojiw3v4vikm">AI প্রসিকিউশন নোটিশ (মূল মুদ্রিত কপি)</option>
                <option data-id="element-1309" data-mp="12qwa77ms75ac">সংশোধিত নোটিশ</option>
                <option data-id="element-1310" data-mp="xqrp14axhmds">সংযুক্তি / প্রমাণপত্র</option>
              </select>
            </label>
            <label className="block" data-id="element-1311" data-mp="1ve0a4y70otf9">
              <span className="mb-1 block text-sm font-medium" data-id="element-1312" data-mp="1vcxhakq4wgwp">মন্তব্য</span>
              <Textarea rows={3} placeholder="প্রয়োজনে মন্তব্য লিখুন" defaultValue="১৪ জুন ২০২৫ এর ব্যাচ-০৩ থেকে নোটিশ" data-id="element-1313" data-mp="1qfyqab1roq2q" />
            </label>
            <div className="flex flex-col gap-2 pt-2 sm:flex-row" data-id="element-1314" data-mp="1llkqzxjzhyss">
              <Button variant="outline" className="flex-1" onClick={upload} disabled={progress !== null || uploaded} data-id="element-1315" data-mp="1gob3ing5nx7l">
                <UploadCloud className="h-4 w-4" data-id="element-1316" data-mp="2e6b7ydmte16i" /> আপলোড করুন
              </Button>
              <Button className="flex-1" disabled={!uploaded} onClick={() => navigate('/admin/ocr')} data-id="element-1317" data-mp="29c6ynnq2th29">
                <ScanLine className="h-4 w-4" data-id="element-1318" data-mp="2gqh8ebc7z9ib" /> AI/OCR প্রক্রিয়া শুরু করুন
              </Button>
            </div>
          </div>
        </Panel>
      </div>
    </div>;
}